import { Box, Divider, Stack } from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { Hangar, ParamSet, PlacementWithLocation, Trip } from "../../types";
import { CurrentAircraft } from "../../widgets/CurrentAircraft/CurrentAircraft";
import { TimelineAircraftList } from "../../widgets/TimelineAircraftList/TimelineAircraftList";
import { HOLDING_AREA_ID } from "../MultiHangar/HoldingArea";
import { CustomPlacementOptions } from "./CustomStackDialog";
import { StackStrategy } from "./GenerateParamSets";
import { HangarStack } from "./Hangar";
import { HangarInfoCard } from "./HangarInfoCard";
import { RunStack } from "./RunStack";
import { useScheduleTimeRange } from "../../stores/useScheduleTimeRange";

type Props = {
  hangar: Hangar;
  setHangar: (hangar: Hangar) => void;
  stacks: HangarStack[];
  setStacks: (stacks: HangarStack[]) => void;
  stackStrategy: StackStrategy;
  setStackStrategy: (s: StackStrategy) => void;
  paramSet: ParamSet;
  customPlacementOptions: CustomPlacementOptions;
  setCustomPlacementOptions: (options: CustomPlacementOptions) => void;
  isAlgorithmTesting?: boolean;
  isReference?: boolean;
  multiHangar?: boolean;
  width?: number;
  viewableHangars?: Hangar[];
  activeHangarId?: string;
  setActiveHangarId?: (id: string) => void;
  readOnly?: boolean;
  disableAutoOpen?: boolean;
  // Scheduled view props
  isScheduledView?: boolean;
  onAddTrip?: (trip: Trip) => void;
  currentTime?: Date;
  timeRange?: { start: Date; end: Date };
  allPlacements?: PlacementWithLocation[];
  onRemoveAircraft?: (tenantId: string, tripId: string) => void;
  onRefresh?: () => void;
  onTimeChange?: (time: Date) => void;
  onNavigateToLocation?: (locationId: string, locationType: string, time: Date) => void;
  onSetCurrentTime?: (date: Date) => void;
};

export const HangarLeftPanel: React.FC<Props> = ({
  hangar,
  setHangar,
  stacks,
  setStacks,
  stackStrategy,
  setStackStrategy,
  paramSet,
  customPlacementOptions,
  setCustomPlacementOptions,
  isReference = false,
  isAlgorithmTesting = false,
  multiHangar = false,
  width = 400,
  viewableHangars,
  activeHangarId,
  setActiveHangarId,
  readOnly = false,
  disableAutoOpen = false,
  isScheduledView = false,
  onAddTrip,
  currentTime,
  timeRange,
  allPlacements,
  onRemoveAircraft,
  onRefresh,
  onTimeChange,
  onNavigateToLocation,
  onSetCurrentTime,
}) => {
  const navigate = useNavigate();
  const { rangeStart, rangeEnd } = useScheduleTimeRange();

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{
        height: "100%",
        display: "flex",
        flex: 1,
        width: width ?? 400,
        overflow: "auto",
      }}
    >
      <HangarInfoCard
        customPlacementOptions={customPlacementOptions}
        setCustomPlacementOptions={setCustomPlacementOptions}
        isAlgorithmTesting={isAlgorithmTesting}
        hangar={hangar}
        onClickBack={() => navigate("/hangars")}
        isReference={isReference}
        multiHangar={multiHangar}
        activeHangarId={activeHangarId}
        setActiveHangarId={setActiveHangarId}
        hangars={viewableHangars}
        isScheduledView={isScheduledView}
        timeRange={timeRange}
        allPlacements={allPlacements}
      />
      {hangar.id !== HOLDING_AREA_ID && (
        <>
          <Divider />
          <RunStack
            disabled={readOnly}
            hangar={hangar}
            isAlgorithmTesting={isAlgorithmTesting}
            customPlacementOptions={customPlacementOptions}
            paramSet={paramSet}
            stacks={stacks}
            setStacks={setStacks}
            stackStrategy={stackStrategy}
            setStackStrategy={setStackStrategy}
            isScheduledView={isScheduledView}
          />
        </>
      )}
      <Box sx={{ height: 8, width: "100%", backgroundColor: "#EEEEEE" }} />
      {isScheduledView && allPlacements ? (
        <TimelineAircraftList
          readOnly={readOnly}
          width={width}
          location={{ type: "hangar", ...hangar }}
          stack={hangar.stack}
          setStack={(stack) => {
            setHangar({
              ...hangar,
              stack: {
                ...hangar.stack,
                ...stack,
              },
            });
          }}
          disableAutoOpen={disableAutoOpen}
          isScheduledView={isScheduledView}
          onAddTrip={onAddTrip}
          currentTime={currentTime || new Date()}
          onRemoveAircraft={onRemoveAircraft}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          placementsInRange={allPlacements.filter(p => {
            const start = new Date(p.start_time).getTime();
            const end = new Date(p.end_time).getTime();
            const rangeStartTime = rangeStart.getTime();
            const rangeEndTime = rangeEnd.getTime();
            return (start >= rangeStartTime && start <= rangeEndTime) ||
                   (end >= rangeStartTime && end <= rangeEndTime) ||
                   (start <= rangeStartTime && end >= rangeEndTime);
          })}
          allPlacementsFromNow={allPlacements}
          onRefresh={onRefresh}
          onTimeChange={onTimeChange}
          onNavigateToLocation={onNavigateToLocation}
          onSetCurrentTime={onSetCurrentTime}
        />
      ) : (
        <CurrentAircraft
          readOnly={readOnly}
          width={width}
          location={{ type: "hangar", ...hangar }}
          stack={hangar.stack}
          setStack={(stack) => {
            setHangar({
              ...hangar,
              stack: {
                ...hangar.stack,
                ...stack,
              },
            });
          }}
          disableAutoOpen={disableAutoOpen}
          isScheduledView={isScheduledView}
          onAddTrip={onAddTrip}
          currentTime={currentTime}
          onRemoveAircraft={onRemoveAircraft}
        />
      )}
    </Stack>
  );
};
